/**
 * EVERY CALL, SEARCHABLE, AND WHAT IT TURNED INTO.
 *
 * Anthony, 2026-10-06: "a tab under the admin panel that lets you compile
 * all phone calls in a visual dashboard with intelligence that lets you
 * search and functions better than callrail. Mainly you can search a
 * keyword related to service and cross reference between dates, time and
 * a really good data set."
 *
 * One request does all of it. `call_intel()` (migration 0198) searches the
 * words on every call, expands a service word into the shop's own family
 * of words for it, and joins each call to the customer with those ten
 * digits and to the job opened for them within fourteen days. That last
 * part is the thing CallRail cannot do: it knows the call, this knows what
 * the call was worth.
 *
 * OWNER AND SUPERVISORS, like Reports. The function checks the same thing,
 * because this gate is in the interface and the interface is not a
 * boundary.
 *
 * Commas split the search into terms that are counted side by side, so
 * "brakes, oil change" is a comparison and not a narrower search. Clicking
 * any bar, cell or phrase narrows the search to it; the chips above the
 * figures say what is narrowed and take it off again.
 */

import { db } from "../data/client.ts";
import { esc, money, mount } from "../ui/dom.ts";
import { callButtonsHtml, wireCallButtons } from "../ui/callplay.ts";
import type { Role } from "../domain/permissions.ts";
import { campaignOf } from "../domain/leadcampaign.ts";

/** The campaign a line belongs to ("Hem-GMB"), same tags as the lead board. */
const lineTag = (line: string | null | undefined) => (line ? campaignOf("callrail", line) : null);

const MAY_SEE: Role[] = ["admin", "supervisor"];

type Term = { n: number; t: string; family: string[]; calls: number; prev: number; jobs: number; money: number };
type Bar = { k: string; calls: number; missed?: number; jobs: number; section?: string };
type Row = {
  id: string; at: string; local: string; dow: number; phone: string | null;
  customer: string | null; customer_id: string | null; secs: number | null;
  outcome: "answered" | "missed" | "voicemail" | "outbound"; source: string | null;
  city: string | null; line: string | null; sentiment: string | null; lead: number | null;
  first: boolean; ref: string | null; rec: boolean; hits: number[]; summary: string | null;
  mark: string | null; job_id: string | null; job_number: number | null; job_title: string | null;
  job_status: string | null; job_money: number;
};
type Intel = {
  from: string; to: string; tz: string; days: number; terms: Term[];
  totals: Record<string, number | null>; prev: Record<string, number>;
  daily: Array<{ d: string; calls: number; missed: number; jobs: number; by_term: number[] }>;
  heat: Array<[number, number, number, number, number]>;
  sources: Bar[]; cities: Bar[]; lines: Bar[]; services: Bar[];
  phrases: Array<{ k: string; calls: number }>; words: Array<{ k: string; calls: number }>;
  sentiment: Record<string, number>; rows: Row[]; limited: boolean;
};

type Filters = {
  outcome?: string; source?: string; city?: string; line?: string; sentiment?: string;
  dows?: number[]; hour_from?: number; hour_to?: number; min_secs?: number;
  first_time?: boolean; converted?: boolean; has_summary?: boolean;
};

/* Kept across visits in one sitting, so going to a job and coming back
   does not throw the search away. */
const state: { q: string; span: string; from: string; to: string; f: Filters; limit: number; heat: 0 | 1 | 2 } = {
  q: "", span: "30", from: "", to: "", f: {}, limit: 100, heat: 0,
};

const SPANS: Array<[string, string]> = [["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["365", "12 months"], ["custom", "Pick dates"]];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const OUTCOME: Record<string, string> = { answered: "Answered", missed: "Missed", voicemail: "Voicemail", outbound: "We called" };

const ymd = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hour12 = (h: number): string => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "a" : "p"}`;
const num = (n: unknown): string => Number(n ?? 0).toLocaleString("en-US");
const pct = (a: number, b: number): string => (b > 0 ? `${Math.round((a / b) * 100)}%` : "—");
const mins = (s: number): string => (s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`);
const phoneFmt = (p: string | null): string => {
  const d = String(p ?? "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : esc(p ?? "");
};

function range(): { from: string; to: string } {
  if (state.span === "custom" && state.from && state.to) return { from: state.from, to: state.to };
  const to = new Date();
  const from = new Date(to);
  from.setDate(to.getDate() - (Number(state.span) || 30) + 1);
  return { from: ymd(from), to: ymd(to) };
}

/** Against the period before it, the same length. */
function delta(now: number, before: number): string {
  if (!before) return now ? "new this period" : "";
  const d = Math.round(((now - before) / before) * 100);
  return `${d > 0 ? "▲" : d < 0 ? "▼" : "•"} ${Math.abs(d)}% vs the ${num(before)} before`;
}

async function ask(limit = state.limit): Promise<Intel> {
  const sb = await db();
  const { from, to } = range();
  const { data, error } = await sb.rpc("call_intel", {
    p_q: state.q, p_from: from, p_to: to, p_f: state.f, p_limit: limit,
  });
  if (error) throw new Error(error.message);
  return data as Intel;
}

export async function callsPage(role: Role): Promise<void> {
  if (!MAY_SEE.includes(role)) {
    mount(`<div class="page"><header class="page-head"><h1>Calls</h1></header>
      <div class="card pad"><p class="quiet">Every call the shop took, and what each one was
      worth, is kept to the owner and supervisors.</p></div></div>`);
    return;
  }
  wireCallButtons();
  if (!state.from) Object.assign(state, range());

  mount(`<div class="page calls-page">
    <header class="page-head">
      <a class="back" href="#/more">&larr; Everything else</a>
      <h1>Calls</h1>
      <p>Every call, searchable by what was said, and what it turned into.</p>
    </header>
    <form class="cx-search card" id="cxform" autocomplete="off">
      <input id="cxq" type="search" enterkeyhint="search" value="${esc(state.q)}"
        placeholder="Search a service, a word or a number. Commas compare: brakes, oil change"
        aria-label="Search calls">
      <button class="btn primary" type="submit">Search</button>
    </form>
    <div class="tabs wide tabs-wrap" id="cxspan">
      ${SPANS.map(([k, l]) => `<button class="tab ${state.span === k ? "on" : ""}" data-span="${k}">${l}</button>`).join("")}
    </div>
    <div class="cx-dates" id="cxdates" ${state.span === "custom" ? "" : "hidden"}>
      <label class="field"><span>From</span><input type="date" id="cxfrom" value="${esc(state.from)}"></label>
      <label class="field"><span>To</span><input type="date" id="cxto" value="${esc(state.to)}"></label>
    </div>
    <div class="cx-filters" id="cxfilters"></div>
    <div id="cxbody"><div class="loading">Reading every call&hellip;</div></div>
  </div>`);

  const body = document.getElementById("cxbody");
  if (!body) return;
  const run = async (): Promise<void> => {
    drawFilters();
    body.innerHTML = `<div class="loading">Reading every call&hellip;</div>`;
    try {
      draw(body, await ask());
    } catch (e) {
      body.innerHTML = `<div class="card"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`;
    }
  };

  document.getElementById("cxform")?.addEventListener("submit", (e) => {
    e.preventDefault();
    state.q = (document.getElementById("cxq") as HTMLInputElement | null)?.value.trim() ?? "";
    state.limit = 100;
    void run();
  });
  document.getElementById("cxspan")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-span]");
    if (!b) return;
    state.span = b.dataset["span"]!;
    document.querySelectorAll("#cxspan .tab").forEach((t) => t.classList.toggle("on", t === b));
    const dates = document.getElementById("cxdates");
    if (dates) dates.hidden = state.span !== "custom";
    if (state.span !== "custom") Object.assign(state, range());
    void run();
  });
  for (const id of ["cxfrom", "cxto"]) {
    document.getElementById(id)?.addEventListener("change", () => {
      state.from = (document.getElementById("cxfrom") as HTMLInputElement).value;
      state.to = (document.getElementById("cxto") as HTMLInputElement).value;
      if (state.from && state.to) void run();
    });
  }

  /* One listener for everything clickable inside the figures. Each one
     narrows the search, and the chip it leaves behind is the way back. */
  const page = document.querySelector<HTMLElement>(".calls-page");
  page?.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-narrow],[data-unset],[data-add],[data-more],[data-csv],[data-heat]");
    if (!el) return;
    e.preventDefault();
    const d = el.dataset;
    if (d["narrow"]) {
      const [k, v] = [d["narrow"], d["v"] ?? ""];
      if (k === "cell") {
        const [dow, hr] = v.split(":").map(Number);
        state.f.dows = [dow!]; state.f.hour_from = hr!; state.f.hour_to = hr!;
      } else if (k === "dow") {
        const n = Number(v);
        const has = state.f.dows ?? [];
        state.f.dows = has.includes(n) ? has.filter((x) => x !== n) : [...has, n].sort();
        if (!state.f.dows.length) delete state.f.dows;
      } else if (k === "first_time" || k === "converted" || k === "has_summary") {
        const cur = state.f[k];
        if (cur === undefined) state.f[k] = true;
        else if (cur) state.f[k] = false;
        else delete state.f[k];
      } else {
        const key = k as "outcome" | "source" | "city" | "line" | "sentiment";
        if (state.f[key] === v) delete state.f[key]; else state.f[key] = v;
      }
      state.limit = 100;
      void run();
    } else if (d["unset"]) {
      const k = d["unset"];
      if (k === "hours") { delete state.f.hour_from; delete state.f.hour_to; }
      else if (k === "q") { state.q = ""; const i = document.getElementById("cxq") as HTMLInputElement | null; if (i) i.value = ""; }
      else if (k === "all") { state.f = {}; }
      else delete (state.f as Record<string, unknown>)[k];
      void run();
    } else if (d["add"]) {
      const words = state.q ? state.q.split(",").map((s) => s.trim()).filter(Boolean) : [];
      if (!words.some((w) => w.toLowerCase() === d["add"]!.toLowerCase()) && words.length < 6) words.push(d["add"]!);
      state.q = words.join(", ");
      const i = document.getElementById("cxq") as HTMLInputElement | null;
      if (i) i.value = state.q;
      state.limit = 100;
      void run();
    } else if (d["more"]) {
      state.limit = Math.min(state.limit * 3, 5000);
      void run();
    } else if (d["csv"] !== undefined) {
      void exportCsv(el as HTMLButtonElement);
    } else if (d["heat"]) {
      state.heat = Number(d["heat"]) as 0 | 1 | 2;
      void run();
    }
  });

  await run();
}

/** What is narrowed, as chips that take themselves off. */
function drawFilters(): void {
  const host = document.getElementById("cxfilters");
  if (!host) return;
  const f = state.f;
  const chip = (label: string, unset: string): string =>
    `<button class="cx-chip on" data-unset="${unset}" title="Take this off">${esc(label)} <span aria-hidden="true">&times;</span></button>`;
  const toggle = (label: string, key: "first_time" | "converted" | "has_summary", yes: string, no: string): string => {
    const v = f[key];
    return `<button class="cx-chip${v === undefined ? "" : " on"}" data-narrow="${key}" aria-pressed="${v !== undefined}">${
      esc(v === undefined ? label : v ? yes : no)}</button>`;
  };
  const set: string[] = [];
  if (f.outcome) set.push(chip(OUTCOME[f.outcome] ?? f.outcome, "outcome"));
  if (f.source) set.push(chip(`From ${f.source}`, "source"));
  if (f.city) set.push(chip(f.city, "city"));
  if (f.line) set.push(chip(lineTag(f.line)?.tag ?? `Line ${f.line.slice(-4)}`, "line"));
  if (f.sentiment) set.push(chip(`${f.sentiment[0]!.toUpperCase()}${f.sentiment.slice(1)} tone`, "sentiment"));
  if (f.hour_from !== undefined) set.push(chip(f.hour_from === f.hour_to
    ? `${hour12(f.hour_from)} hour` : `${hour12(f.hour_from)} to ${hour12(f.hour_to ?? 23)}`, "hours"));

  host.innerHTML = `
    <div class="cx-row">
      ${(["answered", "missed", "voicemail", "outbound"] as const).map((o) =>
        `<button class="cx-chip${f.outcome === o ? " on" : ""}" data-narrow="outcome" data-v="${o}">${OUTCOME[o]}</button>`).join("")}
      <span class="cx-sep"></span>
      ${toggle("New or repeat", "first_time", "New callers only", "Repeat callers only")}
      ${toggle("Became a job or not", "converted", "Became a job", "Did not become a job")}
      ${toggle("Summary or not", "has_summary", "Has a summary", "No summary")}
    </div>
    <div class="cx-row">
      ${DOW.map((d, i) => `<button class="cx-chip sm${f.dows?.includes(i) ? " on" : ""}" data-narrow="dow" data-v="${i}">${d}</button>`).join("")}
      ${set.length ? `<span class="cx-sep"></span>${set.join("")}` : ""}
      ${set.length || f.dows || f.first_time !== undefined || f.converted !== undefined || f.has_summary !== undefined
        ? `<button class="linkbtn" data-unset="all">Clear all</button>` : ""}
    </div>`;
}

function tile(label: string, value: string, note: string, kind = "count"): string {
  return `<div class="mtile ${kind}"><div class="mtile-l">${esc(label)}</div>
    <div class="mtile-v">${esc(value)}</div><div class="mtile-n">${esc(note)}</div></div>`;
}

function draw(host: HTMLElement, r: Intel): void {
  const t = r.totals as Record<string, number>;
  const inbound = t["calls"]! - t["outbound"]!;
  const lost = t["missed"]! + t["voicemail"]!;

  if (!t["calls"]) {
    host.innerHTML = `${termsHtml(r)}<div class="card"><div class="empty">No calls match${
      state.q ? ` &ldquo;${esc(state.q)}&rdquo;` : ""} between ${esc(r.from)} and ${esc(r.to)}.
      ${Object.keys(state.f).length ? `<br><button class="linkbtn" data-unset="all">Take the filters off</button>` : ""}</div></div>`;
    return;
  }

  host.innerHTML = `
    ${termsHtml(r)}
    <div class="mtiles cx-tiles">
      ${tile("Calls", num(t["calls"]), delta(t["calls"]!, r.prev["calls"] ?? 0))}
      ${tile("Answered", pct(t["answered"]!, inbound), `${num(t["answered"])} of ${num(inbound)} inbound`, "rate")}
      ${tile("Missed or voicemail", num(lost), `${pct(lost, inbound)} of inbound. ${delta(lost, (r.prev["calls"] ?? 0) - (r.prev["answered"] ?? 0))}`)}
      ${tile("New callers", num(t["first_time"]), `${num(t["callers"])} different numbers rang`)}
      ${tile("Became a job", num(t["jobs"]), `${pct(t["job_calls"]!, inbound)} of calls led to one within 14 days`, "rate")}
      ${tile("Those jobs paid", money(t["money"]), delta(t["money"]!, r.prev["money"] ?? 0), "money")}
      ${tile("Average answered call", mins(t["avg_secs"] ?? 0), `${num(Math.round((t["talk_secs"] ?? 0) / 3600))} hours on the phone`)}
      ${tile("Known customers", pct(t["known"]!, t["calls"]!), `${num(t["summarised"])} have an AI summary, ${num(t["recorded"])} a recording`)}
    </div>

    <div class="card cx-card">
      <div class="card-head">${r.terms.length > 1 ? "Each search term, day by day" : "Calls day by day"}</div>
      <div class="pad">${dailyHtml(r)}</div>
    </div>

    <div class="cx-grid">
      <div class="card cx-card">
        <div class="card-head cx-head">When they call
          <span class="cx-mini">${["Calls", "Missed", "Became a job"].map((l, i) =>
            `<button class="cx-chip sm${state.heat === i ? " on" : ""}" data-heat="${i}">${l}</button>`).join("")}</span></div>
        <div class="pad">${heatHtml(r)}</div>
      </div>
      <div class="card cx-card">
        <div class="card-head">What they asked for</div>
        <div class="pad">${barsHtml(r.services, "add", "No service words found in the summaries. Calls without a summary cannot be read for this.")}</div>
      </div>
    </div>

    <div class="cx-grid">
      <div class="card cx-card"><div class="card-head">Where they came from</div>
        <div class="pad">${barsHtml(r.sources, "source")}</div></div>
      <div class="card cx-card"><div class="card-head">Which line they rang</div>
        <div class="pad">${barsHtml(r.lines.map((l) => ({ ...l,
          label: `${lineTag(l.k) ? `${esc(lineTag(l.k)!.tag)} ` : ""}${phoneFmt(l.k)}` })), "line")}</div></div>
    </div>

    <div class="cx-grid">
      <div class="card cx-card"><div class="card-head">Where they called from</div>
        <div class="pad">${barsHtml(r.cities, "city", "CallRail gave no city on these calls.")}</div></div>
      <div class="card cx-card"><div class="card-head">The words callers used</div>
        <div class="pad">${wordsHtml(r)}</div></div>
    </div>

    <div class="card cx-card">
      <div class="card-head cx-head">${num(t["calls"])} ${t["calls"] === 1 ? "call" : "calls"}, newest first
        <button class="btn sm" data-csv>Download CSV</button></div>
      ${r.rows.map((row) => rowHtml(row, r)).join("")}
      ${r.limited ? `<div class="pad"><button class="btn wide" data-more="1">Show more calls</button></div>` : ""}
    </div>
    <p class="quiet cx-foot">Times are the shop&rsquo;s own (${esc(r.tz)}). A call &ldquo;became a job&rdquo;
      when a job was opened for a customer with the same number from an hour before the call to
      fourteen days after. Money is the paid and open invoices on those jobs, each job counted once.
      Search reads the AI summary CallRail sends, so a call with no summary is found by its source,
      city and number, not by what was said.</p>`;
  wireTips(host, r);
}

function termsHtml(r: Intel): string {
  if (!r.terms.length) return "";
  return `<div class="cx-terms">${r.terms.map((t) => `
    <div class="cx-term" style="--c:var(--cx${t.n})">
      <div class="cx-term-h"><span class="cx-key"></span><b>${esc(t.t)}</b>
        <span class="cx-term-n">${num(t.calls)} calls &middot; ${num(t.jobs)} jobs &middot; ${money(t.money)}</span></div>
      <div class="cx-term-s">${delta(t.calls, t.prev)}${t.family.length
        ? `. Also matched: ${t.family.map(esc).join(", ")}` : ""}</div>
    </div>`).join("")}
    <button class="linkbtn" data-unset="q">Clear the search</button></div>`;
}

/* ── the day-by-day chart ─────────────────────────────────────────────
   One term or none: stacked columns, answered under missed, so the gap
   in a bad day is visible as orange. Two or more: a line each, because
   stacked terms would add up calls that matched both. */
function dailyHtml(r: Intel): string {
  /* Narrower drawing on a phone, so the chart keeps its height there. */
  const W = window.innerWidth < 640 ? 420 : 900, H = 220, L = 34, B = 24, T = 10;
  const days = r.daily;
  const multi = r.terms.length > 1;
  const peak = Math.max(1, ...days.map((d) => (multi ? Math.max(0, ...d.by_term) : d.calls)));
  const step = niceStep(peak);
  const top = Math.ceil(peak / step) * step;
  const y = (v: number): number => T + (H - T - B) * (1 - v / top);
  const slot = (W - L) / days.length;
  const x = (i: number): number => L + slot * i + slot / 2;
  const grid: string[] = [];
  for (let v = 0; v <= top; v += step) {
    grid.push(`<line x1="${L}" x2="${W}" y1="${y(v)}" y2="${y(v)}" class="cx-grid-l"/>
      <text x="${L - 6}" y="${y(v) + 4}" class="cx-ax" text-anchor="end">${v}</text>`);
  }
  const every = Math.ceil(days.length / 10);
  const labels = days.map((d, i) => i % every === 0
    ? `<text x="${x(i)}" y="${H - 6}" class="cx-ax" text-anchor="middle">${esc(shortDay(d.d, days.length))}</text>` : "").join("");
  let marks = "";
  if (multi) {
    marks = r.terms.map((t, ti) => {
      const pts = days.map((d, i) => `${x(i).toFixed(1)},${y(d.by_term[ti] ?? 0).toFixed(1)}`).join(" ");
      return `<polyline points="${pts}" fill="none" stroke="var(--cx${t.n})" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
    }).join("");
  } else {
    const w = Math.max(2, Math.min(22, slot - 2));
    marks = days.map((d, i) => {
      const ans = d.calls - d.missed;
      const x0 = x(i) - w / 2;
      const yA = y(ans), yM = y(d.calls);
      return `${ans ? `<rect x="${x0}" y="${yA}" width="${w}" height="${Math.max(0, y(0) - yA)}" rx="${Math.min(3, w / 3)}" fill="var(--cx1)"/>` : ""}
        ${d.missed ? `<rect x="${x0}" y="${yM}" width="${w}" height="${Math.max(0, yA - yM - (ans ? 2 : 0))}" rx="${Math.min(3, w / 3)}" fill="var(--cx2)"/>` : ""}`;
    }).join("");
  }
  const hits = days.map((_, i) =>
    `<rect class="cx-hit" data-day="${i}" x="${L + slot * i}" y="${T}" width="${slot}" height="${H - T - B}"/>`).join("");
  const legend = multi
    ? r.terms.map((t) => `<span><i style="background:var(--cx${t.n})"></i>${esc(t.t)}</span>`).join("")
    : `<span><i style="background:var(--cx1)"></i>Answered or we called</span><span><i style="background:var(--cx2)"></i>Missed or voicemail</span>`;
  return `<div class="cx-legend">${legend}</div>
    <div class="cx-chartwrap"><svg class="cx-chart" viewBox="0 0 ${W} ${H}" role="img"
      aria-label="Calls per day from ${esc(r.from)} to ${esc(r.to)}">${grid.join("")}${marks}${labels}
      <line class="cx-cross" x1="0" x2="0" y1="${T}" y2="${H - B}" visibility="hidden"/>${hits}</svg>
      <div class="cx-tip" hidden></div></div>`;
}

function niceStep(peak: number): number {
  const raw = peak / 4;
  const mag = 10 ** Math.floor(Math.log10(Math.max(1, raw)));
  return [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
}

function shortDay(iso: string, span: number): string {
  const [yy, mm, dd] = iso.split("-").map(Number);
  const d = new Date(yy!, mm! - 1, dd!);
  return span > 120
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
}

function wireTips(host: HTMLElement, r: Intel): void {
  const svg = host.querySelector<SVGSVGElement>(".cx-chart");
  const tip = host.querySelector<HTMLElement>(".cx-tip");
  const cross = svg?.querySelector<SVGLineElement>(".cx-cross");
  if (!svg || !tip || !cross) return;
  const show = (e: Event): void => {
    const el = (e.target as Element).closest<SVGRectElement>(".cx-hit");
    if (!el) return;
    const i = Number(el.dataset["day"]);
    const d = r.daily[i];
    if (!d) return;
    const cx = Number(el.getAttribute("x")) + Number(el.getAttribute("width")) / 2;
    cross.setAttribute("x1", String(cx)); cross.setAttribute("x2", String(cx)); cross.removeAttribute("visibility");
    const [yy, mm, dd] = d.d.split("-").map(Number);
    const day = new Date(yy!, mm! - 1, dd!).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    tip.innerHTML = `<b>${esc(day)}</b>${r.terms.length > 1
      ? r.terms.map((t, ti) => `<div><i style="background:var(--cx${t.n})"></i>${esc(t.t)} <b>${d.by_term[ti] ?? 0}</b></div>`).join("")
      : `<div><i style="background:var(--cx1)"></i>Answered <b>${d.calls - d.missed}</b></div>
         <div><i style="background:var(--cx2)"></i>Missed <b>${d.missed}</b></div>`}
      <div class="cx-tip-n">${d.jobs} became a job</div>`;
    tip.hidden = false;
    const box = svg.getBoundingClientRect();
    const px = (cx / svg.viewBox.baseVal.width) * box.width;
    tip.style.left = `${Math.min(Math.max(0, px - 70), box.width - 150)}px`;
  };
  svg.addEventListener("pointermove", show);
  svg.addEventListener("pointerdown", show);
  svg.addEventListener("pointerleave", () => { tip.hidden = true; cross.setAttribute("visibility", "hidden"); });
}

/* ── when they call: day of week by hour ──────────────────────────────
   One hue, light to dark. Only the hours anybody rang are drawn, so a
   shop that never gets a 3am call does not get eight empty rows of it. */
function heatHtml(r: Intel): string {
  const idx = [2, 3, 4][state.heat]!;
  const cells = new Map(r.heat.map((h) => [`${h[0]}:${h[1]}`, h]));
  const hours = r.heat.map((h) => h[1]);
  if (!hours.length) return `<p class="quiet">No calls to place.</p>`;
  const lo = Math.min(...hours), hi = Math.max(...hours);
  const peak = Math.max(1, ...r.heat.map((h) => h[idx]!));
  const order = [1, 2, 3, 4, 5, 6, 0];
  let best: [number, number, number] = [0, 0, 0];
  for (const h of r.heat) if (h[idx]! > best[2]) best = [h[0], h[1], h[idx]!];
  const what = ["calls", "missed", "jobs"][state.heat]!;
  return `<div class="cx-heat" style="grid-template-columns:2.4rem repeat(${hi - lo + 1},minmax(0,1fr))">
      <span></span>${Array.from({ length: hi - lo + 1 }, (_, i) =>
        `<span class="cx-hh">${(lo + i) % 3 === 0 ? hour12(lo + i) : ""}</span>`).join("")}
      ${order.map((d) => `<span class="cx-hd">${DOW[d]}</span>${Array.from({ length: hi - lo + 1 }, (_, i) => {
        const h = cells.get(`${d}:${lo + i}`);
        const v = h ? h[idx]! : 0;
        const a = v ? 0.14 + 0.86 * (v / peak) : 0;
        return `<button class="cx-cell" data-narrow="cell" data-v="${d}:${lo + i}" style="--a:${a.toFixed(2)}"
          title="${DOW[d]} ${hour12(lo + i)}: ${h ? `${h[2]} calls, ${h[3]} missed, ${h[4]} became a job` : "no calls"}"
          aria-label="${DOW[d]} ${hour12(lo + i)}, ${v} ${what}"></button>`;
      }).join("")}`).join("")}
    </div>
    <p class="cx-note">Busiest for ${what}: <b>${DOW[best[0]]} around ${hour12(best[1])}</b> (${best[2]}). Tap a square to see those calls.</p>`;
}

/* ── ranked bars: calls, and the share of them that became a job ───── */
function barsHtml(rows: Array<Bar & { label?: string }>, narrow: string, none = "Nothing to rank."): string {
  if (!rows.length) return `<p class="quiet">${esc(none)}</p>`;
  const peak = Math.max(1, ...rows.map((b) => b.calls));
  const on = (k: string): boolean => (state.f as Record<string, unknown>)[narrow] === k;
  return `<div class="cx-legend"><span><i style="background:var(--cx1)"></i>Calls</span><span><i style="background:var(--cx3)"></i>Became a job</span></div>
    <div class="cx-bars">${rows.map((b) => `
    <button class="cx-bar${on(b.k) ? " on" : ""}" ${narrow === "add" ? `data-add="${esc(b.k)}"` : `data-narrow="${narrow}" data-v="${esc(b.k)}"`}
      title="${esc(b.label ?? b.k)}: ${b.calls} calls, ${b.jobs} became a job${b.missed !== undefined ? `, ${b.missed} missed` : ""}">
      <span class="cx-bar-k">${b.label ?? esc(b.k)}</span>
      <span class="cx-bar-t"><span class="cx-bar-f" style="width:${(b.calls / peak) * 100}%"></span>
        ${b.jobs ? `<span class="cx-bar-j" style="width:${(b.jobs / peak) * 100}%"></span>` : ""}</span>
      <span class="cx-bar-v">${num(b.calls)}<small>${b.calls ? ` &middot; ${pct(b.jobs, b.calls)}` : ""}</small></span>
    </button>`).join("")}</div>`;
}

function wordsHtml(r: Intel): string {
  if (!r.phrases.length && !r.words.length) return `<p class="quiet">None of these calls has an AI summary to read.</p>`;
  const cloud = (xs: Array<{ k: string; calls: number }>): string => {
    const peak = Math.max(1, ...xs.map((x) => x.calls));
    return xs.map((x) => `<button class="cx-word" data-add="${esc(x.k)}" style="--w:${(0.82 + 0.5 * (x.calls / peak)).toFixed(2)}"
      title="${x.calls} calls. Tap to search it">${esc(x.k)} <small>${x.calls}</small></button>`).join("");
  };
  return `${r.phrases.length ? `<div class="cx-sub">Phrases</div><div class="cx-words">${cloud(r.phrases)}</div>` : ""}
    ${r.words.length ? `<div class="cx-sub">Words</div><div class="cx-words">${cloud(r.words)}</div>` : ""}
    ${sentimentNote(r)}`;
}

function sentimentNote(r: Intel): string {
  const s = r.sentiment;
  const known = (s["positive"] ?? 0) + (s["neutral"] ?? 0) + (s["negative"] ?? 0);
  if (!known) return "";
  return `<div class="cx-sub">Tone, where CallRail judged it</div><div class="cx-row">${
    (["positive", "neutral", "negative"] as const).map((k) =>
      `<button class="cx-chip sm${state.f.sentiment === k ? " on" : ""}" data-narrow="sentiment" data-v="${k}">${k[0]!.toUpperCase()}${k.slice(1)} ${s[k] ?? 0}</button>`).join("")}</div>`;
}

function rowHtml(c: Row, r: Intel): string {
  const when = new Date(c.at).toLocaleString("en-US", {
    timeZone: r.tz, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
  const text = c.mark
    ? esc(c.mark).replace(/\[\[/g, "<mark>").replace(/\]\]/g, "</mark>")
    : esc(c.summary ?? "");
  const who = c.customer_id
    ? `<a href="#/customer/${esc(c.customer_id)}">${esc(c.customer ?? "Customer")}</a>`
    : `<span>${phoneFmt(c.phone)}</span>`;
  return `<div class="row cx-callrow">
    <div class="row-main">
      <div class="row-title">${who}
        <span class="pill cx-o-${c.outcome}">${OUTCOME[c.outcome] ?? c.outcome}</span>
        ${c.first && c.outcome !== "outbound" ? `<span class="pill">new caller</span>` : ""}
        ${c.outcome !== "outbound" && lineTag(c.line)
          ? `<span class="pill" title="${esc(lineTag(c.line)!.name)}">${esc(lineTag(c.line)!.tag)}</span>` : ""}
        ${c.hits.map((n) => `<span class="cx-dot" style="--c:var(--cx${n})" title="${esc(r.terms.find((t) => t.n === n)?.t ?? "")}"></span>`).join("")}
      </div>
      <div class="row-sub">${esc(when)}${c.secs ? ` &middot; ${mins(c.secs)}` : ""}${
        c.source ? ` &middot; ${esc(c.source)}` : ""}${c.city ? ` &middot; ${esc(c.city)}` : ""}${
        c.lead !== null ? ` &middot; lead score ${c.lead}` : ""}${c.sentiment ? ` &middot; ${esc(c.sentiment)}` : ""}</div>
      ${text ? `<div class="row-note cx-said">${text}</div>` : ""}
      ${c.ref ? callButtonsHtml(c.ref) : ""}
    </div>
    <div class="row-side">
      ${c.job_id ? `<a class="cx-job" href="#/job/${esc(c.job_id)}">Job ${c.job_number ? `#${esc(c.job_number)}` : ""}
        <small>${esc(c.job_title ?? c.job_status ?? "")}</small>
        ${c.job_money ? `<b>${money(c.job_money)}</b>` : ""}</a>` : ""}
    </div>
  </div>`;
}

/* ── CSV, of everything that matched, up to five thousand ─────────── */
async function exportCsv(b: HTMLButtonElement): Promise<void> {
  const was = b.textContent;
  b.disabled = true; b.textContent = "Gathering…";
  try {
    const r = await ask(5000);
    const head = ["When", "Day", "Outcome", "Seconds", "Phone", "Customer", "New caller", "Source", "City", "Line", "Campaign",
      "Sentiment", "Lead score", "Matched", "Summary", "Job number", "Job", "Job paid", "Call id"];
    const cell = (v: unknown): string => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = r.rows.map((c) => [c.local, DOW[c.dow], OUTCOME[c.outcome], c.secs ?? "", c.phone ?? "", c.customer ?? "",
      c.first ? "yes" : "", c.source ?? "", c.city ?? "", c.line ?? "", lineTag(c.line)?.tag ?? "", c.sentiment ?? "", c.lead ?? "",
      c.hits.map((n) => r.terms.find((t) => t.n === n)?.t ?? "").join("; "), c.summary ?? "",
      c.job_number ?? "", c.job_title ?? "", c.job_money || "", c.ref ?? ""].map(cell).join(","));
    const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `calls-${r.from}-to-${r.to}${state.q ? `-${state.q.replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}` : ""}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    b.textContent = r.limited ? "Downloaded the newest 5,000" : was;
  } catch (e) {
    b.textContent = e instanceof Error ? e.message : "Could not download";
  }
  b.disabled = false;
}
